/**
 * Разговоры с ассистентом: всё, кроме самого хода.
 *
 * Ход отдаётся потоком и живёт в stream.ts — здесь обычные вызовы через общий
 * axios: тред заводится один раз на контекст, а остановка это короткий POST,
 * которому поток не нужен.
 */
import { assistantApi, http, issueApi } from '@/api'
import { TrackerAssistantContextType, TrackerAssistantRole } from '@/api/clients/tracker-assistant'

import { threadPath, type AssistantQuota } from './stream'

export type AssistantContextType = TrackerAssistantContextType

/**
 * Где находится человек, когда спрашивает.
 *
 * Дескриптор нарочно лёгкий: тип, ключ и подпись для чипа. Содержимое задачи
 * или доски модель добирает инструментами сама — заталкивать его в контекст
 * значит слать в Gemini то, о чём не спрашивали.
 */
export interface AssistantContext {
  type: AssistantContextType
  /** Ключ задачи, ключ проекта или идентификатор страницы; пусто для NONE */
  key: string
  /** Что показать в чипе контекста: «Разработка · Доска» */
  label: string
}

export const emptyContext = (): AssistantContext => ({
  type: TrackerAssistantContextType.AssistantContextTypeNone,
  key: '',
  label: '',
})

export const createThread = async (context: AssistantContext): Promise<string> => {
  const { data } = await assistantApi.assistantServiceCreateThread({
    contextType: context.type,
    contextKey: context.key,
  })

  return data.id ?? ''
}

/** Разговор в списке истории: заголовок берётся из первого вопроса. */
export interface ThreadBrief {
  id: string
  title: string
  updatedAt: string
}

/**
 * Прошлые разговоры об этом же месте.
 *
 * Отбор по контексту, а не весь список: на доске проекта интересны разговоры о
 * ней, и мешать их с обсуждением чужой задачи значит сделать переключатель
 * бесполезным.
 */
export const listThreads = async (context: AssistantContext): Promise<ThreadBrief[]> => {
  const { data } = await assistantApi.assistantServiceListThreads(context.type, context.key, 20)

  return (data.threads ?? []).map((thread) => ({
    id: thread.id ?? '',
    title: thread.title ?? '',
    updatedAt: thread.updatedAt ?? '',
  }))
}

/** Реплика разговора в том виде, в каком её показывает панель. */
export interface ThreadMessage {
  id: number
  role: 'user' | 'assistant'
  text: string
}

/**
 * Сообщения выбранного разговора.
 *
 * Строки инструментов сюда не попадают: панель каркаса показывает только текст,
 * а вызовы получат свой вид в DEV-1557.
 */
export const listMessages = async (threadId: string): Promise<ThreadMessage[]> => {
  const { data } = await assistantApi.assistantServiceListMessages(threadId)

  return (data.messages ?? [])
    .filter((message) => message.content && message.role !== TrackerAssistantRole.AssistantRoleTool)
    .map((message, index) => ({
      id: index + 1,
      role:
        message.role === TrackerAssistantRole.AssistantRoleUser
          ? ('user' as const)
          : ('assistant' as const),
      text: message.content ?? '',
    }))
}

/**
 * Остановить идущий ход.
 *
 * Отдельный вызов, а не расчёт на обрыв соединения: между браузером и сервисом
 * ингресс и прокси, и уход клиента долетает до серверного контекста когда
 * придётся. Пока не долетел — горят и дневной лимит человека, и квота
 * ai-gateway (DEV-1553).
 */
export const cancelTurn = async (threadId: string): Promise<void> => {
  await http.post(`${threadPath(threadId)}/cancel`)
}

/**
 * Остаток обращений на сегодня.
 *
 * Отдельный запрос, потому что в потоке остаток приезжает первым событием — то
 * есть уже в ответ на вопрос. Решение «тратить ли последнее обращение» человек
 * принимает до того, как сформулирует вопрос, а не после (макет 8-1).
 */
export const fetchQuota = async (): Promise<AssistantQuota> => {
  const { data } = await http.get<AssistantQuota>('/api/v1/tracker/assistant/quota')

  return data
}

/** Похожая задача под черновиком создания (DEV-1561). */
export interface SimilarIssue {
  key: string
  title: string
}

/** Сколько похожих задач показать: список под черновиком, а не выдача поиска. */
const SIMILAR_LIMIT = 3

/**
 * Похожие задачи по заголовку будущей.
 *
 * Тем же полнотекстовым поиском, каким ищет человек: отдельного «поиска дублей»
 * в трекере нет, а заводить его ради подсказки — это второй индекс и второе
 * место, где он разъедется с первым.
 *
 * Отказ поиска гасится молча: подсказка не должна мешать подтвердить черновик.
 */
export const findSimilarIssues = async (title: string): Promise<SimilarIssue[]> => {
  const text = title.trim()

  if (!text) return []

  try {
    const { data } = await issueApi.issueServiceSearchIssues(
      text,
      undefined,
      '1',
      String(SIMILAR_LIMIT),
    )

    return (data.issues ?? []).map((issue) => ({ key: issue.key ?? '', title: issue.title ?? '' }))
  } catch {
    return []
  }
}

/** Приёмы инлайн-помощи редактора — те же имена, что знает бэкенд (DEV-1578). */
export type InlineAction = 'acceptance_criteria' | 'subtasks'

export interface InlineAssistParams {
  action: InlineAction
  text: string
  /** Откуда правят: гейт ai_enabled спрашивает про задачу или проект */
  issue?: string
  project?: string
}

/**
 * Инлайн-помощь редактора: на входе выделенный текст, на выходе — текст.
 *
 * Обычный POST, а не поток: ответ показывают сравнением «до и после» целиком, и
 * по буквам его никто не читает.
 */
export const inlineAssist = async (params: InlineAssistParams): Promise<string> => {
  const { data } = await http.post<{ text: string; quota: AssistantQuota }>(
    '/api/v1/tracker/assistant/inline',
    params,
  )

  return data.text ?? ''
}

/**
 * Отметка о переходе по источнику ответа (DEV-1564).
 *
 * Ошибку глотаем: человек уже кликнул и уходит на страницу, и мешать ему
 * сообщением о ненаписанной строке журнала не за что.
 */
export const reportSourceClick = async (ref: string): Promise<void> => {
  try {
    await http.post('/api/v1/tracker/assistant/sources/click', { ref })
  } catch {
    // намеренно тихо
  }
}
